import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { COLORS, LABELS } from '../../constants/config';
import { Card, PageContainer, Navbar, Breadcrumb } from '../../components';

import { tokens } from '@design';
export default function ProfileScreen() {
  const { user, logout } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  // Theme colors
  const themeColors = {
    name: colors.textPrimary,
    userType: colors.info,
    org: colors.textMuted,
    infoValue: colors.textPrimary,
    infoLabel: colors.textMuted,
    border: colors.border,
    permissionText: colors.textPrimary,
    actionText: colors.textPrimary,
    iconDefault: colors.iconDefault,
  };

  const handleLogout = () => {
    Alert.alert(
      t('auth.logout'),
      t('auth.logoutConfirm'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('auth.logout'),
          style: 'destructive',
          onPress: logout,
        },
      ]
    );
  };

  if (!user) {
    return null;
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('navigation.home') },
          { label: t('navigation.profile') },
        ]}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
      {/* Profile Header */}
      <Card>
        <View style={styles.header}>
          {user.profile?.profile_image ? (
            <Image
              source={{ uri: user.profile.profile_image }}
              style={styles.avatar}
            />
          ) : (
            <View style={styles.avatarPlaceholder}>
              <Ionicons name="person" size={48} color={COLORS.white} />
            </View>
          )}
          <Text style={[styles.name, { color: themeColors.name }]}>{user.full_name || user.username}</Text>
          <Text style={[styles.userType, { color: themeColors.userType }]}>{user.profile?.user_type_display}</Text>
          {user.profile?.organization_name && (
            <Text style={[styles.org, { color: themeColors.org }]}>{user.profile.organization_name}</Text>
          )}
        </View>
      </Card>

      {/* User Info */}
      <Card title={t('profile.personalInfo')}>
        <InfoRow icon="person-outline" label={t('profile.username')} value={user.username} themeColors={themeColors} primaryColor={colors.primary} />
        <InfoRow icon="mail-outline" label={t('profile.email')} value={user.email || '-'} themeColors={themeColors} primaryColor={colors.primary} />
        <InfoRow icon="call-outline" label={t('profile.phone')} value={user.profile?.phone || '-'} themeColors={themeColors} primaryColor={colors.primary} />
        <InfoRow icon="briefcase-outline" label={t('profile.jobTitle')} value={user.profile?.job_title || '-'} themeColors={themeColors} primaryColor={colors.primary} />
        <InfoRow icon="calendar-outline" label={t('profile.registrationDate')} value={user.date_joined?.split('T')[0] || '-'} themeColors={themeColors} primaryColor={colors.primary} />
      </Card>

      {/* Permissions */}
      <Card title={t('profile.permissions')}>
        <View style={[styles.permissionRow, { borderBottomColor: themeColors.border }]}>
          <Ionicons
            name={user.profile?.can_view_all_organizations ? 'checkmark-circle' : 'close-circle'}
            size={20}
            color={user.profile?.can_view_all_organizations ? COLORS.success : COLORS.danger}
          />
          <Text style={[styles.permissionText, { color: themeColors.permissionText }]}>{t('profile.viewAllOrganizations')}</Text>
        </View>
        <View style={[styles.permissionRow, { borderBottomColor: themeColors.border }]}>
          <Ionicons
            name={user.profile?.user_type === 'strategy_team' ? 'checkmark-circle' : 'close-circle'}
            size={20}
            color={user.profile?.user_type === 'strategy_team' ? COLORS.success : COLORS.danger}
          />
          <Text style={[styles.permissionText, { color: themeColors.permissionText }]}>{t('profile.manageSystem')}</Text>
        </View>
      </Card>

      {/* Actions */}
      <Card title={t('profile.actions')}>
        <TouchableOpacity style={[styles.actionRow, { borderBottomColor: themeColors.border }]}>
          <Ionicons name="chevron-back" size={20} color={themeColors.iconDefault} />
          <View style={styles.actionContent}>
            <Text style={[styles.actionText, { color: themeColors.actionText }]}>{t('profile.changePassword')}</Text>
            <Ionicons name="key-outline" size={20} color={colors.primary} />
          </View>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionRow, { borderBottomColor: themeColors.border }]}>
          <Ionicons name="chevron-back" size={20} color={themeColors.iconDefault} />
          <View style={styles.actionContent}>
            <Text style={[styles.actionText, { color: themeColors.actionText }]}>{t('profile.notificationSettings')}</Text>
            <Ionicons name="notifications-outline" size={20} color={colors.primary} />
          </View>
        </TouchableOpacity>
      </Card>

      {/* Logout */}
      <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
        <Ionicons name="log-out-outline" size={24} color={COLORS.white} />
        <Text style={styles.logoutText}>{t('auth.logout')}</Text>
      </TouchableOpacity>
      </ScrollView>
    </PageContainer>
  );
}

function InfoRow({ icon, label, value, themeColors, primaryColor }: { icon: keyof typeof Ionicons.glyphMap; label: string; value: string; themeColors: any; primaryColor: string }) {
  return (
    <View style={[styles.infoRow, { borderBottomColor: themeColors.border }]}>
      <Text style={[styles.infoValue, { color: themeColors.infoValue }]}>{value}</Text>
      <View style={styles.infoLabel}>
        <Text style={[styles.infoLabelText, { color: themeColors.infoLabel }]}>{label}</Text>
        <Ionicons name={icon} size={18} color={primaryColor} style={styles.infoIcon} />
      </View>
    </View>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["4"],
  },
  header: {
    alignItems: 'center',
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    marginBottom: tokens.spacing.absolute["4"],
  },
  avatarPlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: COLORS.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  name: {
    fontSize: 22,
    fontWeight: 'bold',
    color: COLORS.text,
  },
  userType: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.secondary,
    marginTop: tokens.spacing.absolute["1"],
  },
  org: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.textMuted,
    marginTop: tokens.spacing.absolute["1"],
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: COLORS.border,
  },
  infoLabel: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoLabelText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.textMuted,
  },
  infoIcon: {
    marginStart: tokens.spacing.absolute["2"],
  },
  infoValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.text,
    fontWeight: '500',
  },
  permissionRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: COLORS.border,
  },
  permissionText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.text,
    marginEnd: tokens.spacing.absolute["3"],
    flex: 1,
    textAlign: getTextAlign(isRTL),
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["3_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: COLORS.border,
  },
  actionContent: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  actionText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.text,
    marginEnd: tokens.spacing.absolute["3"],
  },
  logoutButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.danger,
    padding: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
    marginTop: tokens.spacing.absolute["2"],
  },
  logoutText: {
    color: COLORS.white,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    marginEnd: tokens.spacing.absolute["2"],
  },
});

const styles = getStyles(true);
